// SocketProvider.tsx
import React, {createContext, useContext, useEffect, useState} from 'react';
import {io, Socket} from 'socket.io-client';
import {receiveMessage, socketConnected, socketDisconnected, socketError,} from '../features/socket/socket_slice';
import {getLS} from '../helpers/ls';
import {fetchNotificationsCount, fetchUserNotifications} from "../features/notifications/notification_slice";
import {useAppDispatch} from "../hooks/useAppDispatch";

export const SocketContext = createContext<Socket | null>(null);

export const SocketProvider = ({children}: { children: React.ReactNode }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const dispatch = useAppDispatch();
    const authToken = getLS('access_token');

    useEffect(() => {
        const socketInstance = io(process.env.REACT_APP_SOCKET_URL as string, {
            auth: {
                token: authToken || null,
            },
        });

        // Handle connection
        socketInstance.on('connect', () => {
            console.log('Socket connected', socketInstance.id);
            dispatch(socketConnected());
        });

        // Handle disconnection
        socketInstance.on('disconnect', () => {
            console.log('Socket disconnected');
            dispatch(socketDisconnected());
        });

        // Handle errors
        socketInstance.on('connect_error', (err) => {
            console.log('Connection error:', err.message);
            dispatch(socketError(err.message));
        });

        setSocket(socketInstance);

        // Cleanup on unmount
        return () => {
            // Disconnect the socket
            socketInstance.disconnect();
            // Off any event listeners
            socketInstance.off('connect');
            socketInstance.off('disconnect');
            socketInstance.off('connect_error');
        };
    }, [dispatch, authToken]);

    useEffect(() => {
        if (!socket) {
            return;
        }

        // Handle general messages
        socket.on('message', (data) => {
            // console.log('Received message:', data);
            // dispatch(receiveMessage(data));
            // TODO: Handle message data later
        });

        // Handle incoming notifications
        socket.on('new_notification', () => {
            dispatch(fetchNotificationsCount());
            dispatch(fetchUserNotifications({page: 1, limit: 10}));
        });

        return () => {
            console.log('Cleaning up socket listeners');
            // Cleanup on unmount
            socket.off('message');
            socket.off('new_notification');
        };
    }, [socket, dispatch]);

    // Optionally render a loading state or null
    if (!socket) {
        // Optionally render a loading state or null
        return null;
    }

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (): Socket => {
    const socket = useContext(SocketContext);
    if (!socket) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return socket;
};

export default SocketProvider;
